import { injectable, interfaces } from 'inversify';
import { action, IObservableArray, makeObservable, observable } from 'mobx';
import { reorderArray } from '../../../../helpers/array.helpers';
import { InputStatus } from '../../../../models/error.model';
import { ImageScopeName } from '../../../annotation/question.model';
import { ProjectStatus } from '../../../projects/projects.model';
import { IAnnotationType, IQuestion, IToolAffect, IToolUsage } from './projectDetailsTools.models';

export interface IProjectDetailsToolsStore {
  readonly id: string;
  readonly status: ProjectStatus;
  readonly annotationTypes: IObservableArray<IAnnotationType>;
  readonly questions: IObservableArray<IQuestion>;
  readonly toolsUsage: IToolUsage | undefined;
  readonly toolsStatus: InputStatus;
  readonly wasValidated: boolean;
  readonly toolAffect: IToolAffect | undefined;
  setRetrievedData(id: string, status: ProjectStatus, annotationTypes: IAnnotationType[], questions: IQuestion[]): void;
  setToolUsage(toolsUsage: IToolUsage): void;
  setValidationStatus(toolsStatus: InputStatus): void;
  reorderQuestions(questionId: string, index: number, isAttribute: boolean): void;
  reorderAnnotationTypes(annotationTypeId: string, index: number): void;
  resetStore(): void;
  setToolAffect(toolsAffect: IToolAffect): void;
}

export namespace IProjectDetailsToolsStore {
  export const $: interfaces.ServiceIdentifier<IProjectDetailsToolsStore> = Symbol('PROJECT_DETAILS_TOOLS_STORE');
}

@injectable()
export class ProjectDetailsToolsStore implements IProjectDetailsToolsStore {
  id: string = '';

  status: ProjectStatus = ProjectStatus.Draft;

  annotationTypes: IObservableArray<IAnnotationType> = observable.array([]);

  questions: IObservableArray<IQuestion> = observable.array([]);

  toolsUsage: IToolUsage | undefined = undefined;

  toolsStatus: InputStatus = InputStatus.empty();

  wasValidated: boolean = false;

  toolAffect: IToolAffect | undefined = undefined;

  constructor() {
    makeObservable(this, {
      id: observable,
      status: observable,
      annotationTypes: observable,
      questions: observable,
      toolsUsage: observable,
      toolsStatus: observable,
      wasValidated: observable,
      toolAffect: observable,
      setRetrievedData: action,
      setToolUsage: action,
      reorderAnnotationTypes: action,
      reorderQuestions: action,
      setValidationStatus: action,
      resetStore: action,
      setToolAffect: action,
    });
  }

  setRetrievedData(id: string, status: ProjectStatus, annotationTypes: IAnnotationType[], questions: IQuestion[]): void {
    this.id = id;
    this.status = status;
    this.annotationTypes.replace(annotationTypes);
    this.questions.replace(questions);
  }

  setToolUsage(toolsUsage: IToolUsage): void {
    this.toolsUsage = toolsUsage;
  }

  reorderAnnotationTypes(annotationTypeId: string, index: number): void {
    const order = this.annotationTypes[index].order;

    this.annotationTypes.replace(reorderArray(this.annotationTypes, annotationTypeId, order));
  }

  reorderQuestions(questionId: string, index: number, isAttribute: boolean): void {
    const toReorder = this.questions.filter((q) => (isAttribute ? !q.scopes.includes(ImageScopeName) : q.scopes.includes(ImageScopeName))).slice();
    const others = this.questions.filter((q) => (isAttribute ? q.scopes.includes(ImageScopeName) : !q.scopes.includes(ImageScopeName))).slice();

    const order = toReorder[index].order;

    const ordered = reorderArray(toReorder, questionId, order);

    this.questions.replace([...ordered, ...others]);
  }

  setValidationStatus(toolsStatus: InputStatus): void {
    this.toolsStatus = toolsStatus;
    this.wasValidated = !toolsStatus.isEmpty;
  }

  resetStore(): void {
    this.id = '';
    this.status = ProjectStatus.Draft;
    this.annotationTypes.replace([]);
    this.questions.replace([]);
    this.toolsUsage = undefined;
    this.toolsStatus = InputStatus.empty();
    this.wasValidated = false;
  }

  setToolAffect(toolsAffect: IToolAffect): void {
    this.toolAffect = toolsAffect;
  }
}
